import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, Injector, OnDestroy, inject, signal, resource, computed, setTestabilityGetter } from "@angular/core";
import { Router } from "@angular/router";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { Keepalive } from "@ng-idle/keepalive";
import { firstValueFrom, Observable, Subject, Subscription } from "rxjs";
import { AppSettingsService, type AppSettings } from "../app-settings-service/app-settings.service";
import { BroadcastService } from "../broadcast-service/broadcast.service";
import { IframeMessage, IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import * as Sentry from "@sentry/browser";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { EncryptedPayload, SessionData } from "./auth.interfaces";

export interface CurrentUser {
    sub: string;
    name: string;
    given_name: string;
    family_name: string;
    locale: string;
    email: string;
    firm_id: string;
    csrf: string;
    sid: string;
    ifirm_userid: string;
    ifirm_url: string;
    ifirm_session_name?: string;
    role?: string | string[];
    tracking_licence_enabled: string;
    can_manage_general_settings: string;
    can_manage_dashboards_management_settings: string;
    can_delete_cards: string;
    can_bulk_change_cards: string;
    can_print_export: string;
    can_edit_assignee: string;
    dashboard_list: string[];
    can_contacts_view_add_edit: string;
    can_client_portal_admin: string;
    can_jobs_view_my: string;
    can_jobs_view_all: string;
    can_jobs_view_add_edit: string;
    can_jobs_view_edit_all_job_resources: string;
    can_jobs_view_edit_my_job_resources: string;
    can_workflow_resource: string;
    summary_view_new_ui_enabled: string;
    can_dashboard_management_create: string;
}

@Injectable()
export class AuthService implements OnDestroy {
    private http = inject(HttpClient);
    private injector = inject(Injector);
    private appSettingsService = inject(AppSettingsService);
    private idle = inject(Idle);
    private keepalive = inject(Keepalive);
    private iframeMessagesService = inject(IframeMessagesService);

    public apiUrl: string;
    public currentUser: CurrentUser | null = null;
    _currentUser = signal<CurrentUser | null>(null);
    private filePathKey: string;
    private userInfoKey: string;
    currentlyReloading = false;
    logoutToScreen = "normal"; // or demo
    sessionData: SessionData = {
        sessionExpiration: 1200,
        site: "",
        sessionName: "",
        userId: "",
    };
    private sessionDataLoadPromise: Promise<SessionData> | null = null;
    userInfoFetched = false;
    bufferTime = 20;

    public userLogoutSubject = new Subject<void>();
    userLogoutEvent$ = this.userLogoutSubject.asObservable();
    public fetchedUserInfoSubject = new Subject<boolean>();
    fetchedUserInfoEvent$ = this.fetchedUserInfoSubject.asObservable();
    private router: Router;
    subscriptions: Subscription[] = [];
    private sessionInitialized = false;
    public sessionInitializedSubject = new Subject<boolean>();
    sessionInitializedEvent$ = this.sessionInitializedSubject.asObservable();
    public longOperationEmitter = new Subject<boolean>();
    longOperationEvent$ = this.longOperationEmitter.asObservable();
    private longOperationIntervalId: ReturnType<typeof setInterval> | undefined;
    public sessionUpdatedReceivedEmitter = new Subject();
    sessionUpdatedReceivedEvent$ = this.sessionUpdatedReceivedEmitter.asObservable();
    public signalRUnauthorizedEmitter = new Subject();
    signalRUnauthorizedEvent$ = this.signalRUnauthorizedEmitter.asObservable();
    public sessionChangedReceivedEmitter = new Subject<boolean>();
    sessionChangedReceivedEvent$ = this.sessionChangedReceivedEmitter.asObservable();

    private sessionDataResource = resource<SessionData | undefined, AppSettings | null>({
        defaultValue: undefined,
        params: () => this.appSettingsService.sAppSettings(),
        loader: ({ params: appSettings }: { params: AppSettings | null }) => {
            if (!appSettings) return Promise.resolve(undefined);
            return this.getLoginWithSession();
        },
    });
    public sSessionData = computed(() => this.sessionDataResource.value());
    public inReloginPrompted = false;

    constructor() {
        this.registerIframeMessagesBroadcast();
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
    }

    async getFilePathKey(): Promise<string> {
        if (!this.filePathKey) {
            const request = this.http.get(this.apiUrl + "key/filePathKey", {
                responseType: "text",
            });
            this.filePathKey = await firstValueFrom(request);
        }
        return this.filePathKey;
    }

    async getUserInfoKey(): Promise<string> {
        if (!this.userInfoKey) {
            const request = this.http.get(this.apiUrl + "key/userInfoKeyV1", {
                responseType: "text",
            });
            this.userInfoKey = await firstValueFrom(request);
        }
        return this.userInfoKey;
    }

    registerIframeMessagesBroadcast() {
        this.subscriptions.push(
            BroadcastService.on<IframeMessage>("sessionChanged").subscribe((message) => {
                this.sessionData.sessionName = message.data.sessionName as string;
                this.resetIdleWatch(true);
                this.keepalive.start();
                this.inReloginPrompted = false;
                this.sessionChangedReceivedEmitter.next(true);
            }),
        );
        this.subscriptions.push(
            BroadcastService.on<IframeMessage>("sessionUpdated").subscribe(() => {
                this.sessionUpdatedReceivedEmitter.next(true);
            }),
        );
        this.subscriptions.push(
            BroadcastService.on<IframeMessage>("reloginPrompted").subscribe((message) => {
                this.sessionChangedReceivedEmitter.next(false);
                this.inReloginPrompted = true;
                this.keepalive.stop();
            }),
        );
    }

    init(): void {
        this.apiUrl = this.appSettingsService.appSettings.AuthService_URL + this.appSettingsService.appSettings.AuthService_Version;
        this.router = this.injector.get(Router);

        if (this.iframeMessagesService.initDone) {
            this.checkMessageOriginValid();
        } else {
            this.subscriptions.push(this.iframeMessagesService.InitDoneEvent$.subscribe(() => this.checkMessageOriginValid()));
        }

        this.subscriptions.push(
            this.longOperationEvent$.subscribe((e) => {
                if (e) {
                    this.resetIdleWatch(true);
                    this.keepalive.ping();
                    this.longOperationIntervalId = setInterval(() => {
                        this.resetIdleWatch(true);
                    }, this.sessionData.sessionExpiration / 2);
                } else {
                    if (this.longOperationIntervalId) {
                        clearInterval(this.longOperationIntervalId);
                        this.longOperationIntervalId = undefined;
                    }
                }
            }),
        );

        this.idle.setIdle(30);
        this.idle.setTimeout(this.sessionData.sessionExpiration - 100);
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        this.subscriptions.push(
            this.idle.onTimeout.subscribe(() => {
                this.keepalive.stop();
            }),
        );

        this.keepalive.interval(Math.max(this.sessionData.sessionExpiration - 100, 300));
        this.subscriptions.push(
            this.keepalive.onPing.subscribe(() => {
                this.iframeMessagesService.postMessage("updateSession", null);
            }),
        );
    }

    checkMessageOriginValid() {
        // Validate InitSession
        if (this.iframeMessagesService.initSessionInfoOrigin != undefined) {
            if (!this.iframeMessagesService.messageOriginValid(this.iframeMessagesService.initSessionInfoOrigin)) {
                // Not a valid sender, clear the InitSession data
                this.iframeMessagesService.initSessionInfoOrigin = null;
                this.sessionData = {
                    sessionExpiration: 1200,
                    site: "",
                    sessionName: "",
                    userId: "",
                };
            }
        }
    }

    loggedIn() {
        return this.sessionInitialized;
    }

    public async getLoginWithSession(): Promise<SessionData> {
        if (!this.sessionDataLoadPromise) this.sessionDataLoadPromise = this.loginWithSession();
        return this.sessionDataLoadPromise;
    }

    private async loginWithSession() {
        const result = await this.iframeMessagesService.postMessageResp("initSession", null, "initSessionInfo");
        this.sessionData.site = result.site as string;
        this.sessionData.sessionName = result.sessionName as string;
        this.sessionData.userId = result.userId as string;
        this.sessionData.sessionExpiration = (result.sessionExpiration as number) * 60;
        if (this.iframeMessagesService.initDone) {
            this.checkMessageOriginValid();
        }
        if (this.sessionData.site && this.sessionData.sessionName && this.sessionData.userId) {
            await firstValueFrom(this.getUserInfo());
            this.sessionInitialized = true;
            this.sessionInitializedSubject.next(true);
            this.resetIdleWatch(true);

            const emailHash = this.bytesArrToHex(await this.sha256(this.currentUser.email));
            const dashboardsService = this.injector.get(DashboardsService);
            Sentry.getCurrentScope().setUser({
                id: this.currentUser.sub,
                email: emailHash,
                firm_id: this.currentUser.firm_id,
                dashboard_id: dashboardsService.getCurrentDashboardId(),
            });
        }
        return this.sessionData;
    }

    resetIdleWatch(start: boolean) {
        this.idle.watch(!start);
    }

    concat(arr1, arr2) {
        const mergedArray = new Uint8Array(arr1.length + arr2.length);
        mergedArray.set(arr1);
        mergedArray.set(arr2, arr1.length);
        return mergedArray;
    }

    base64ToBytes(base64) {
        const binaryString = window.atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
    }

    async sha256(message) {
        const encoder = new TextEncoder();
        const data = encoder.encode(message);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        return new Uint8Array(hashBuffer);
    }

    async decryptData(toDecrypt) {
        let result = "";
        if (toDecrypt.startsWith("v001~")) {
            toDecrypt = toDecrypt.substring(5);
        }

        if (result === "") {
            const hashedKey = await this.sha256(await this.getUserInfoKey());
            const bytes = this.base64ToBytes(toDecrypt);
            const key = await window.crypto.subtle.importKey("raw", hashedKey, "AES-GCM", true, ["decrypt"]);
            const nonceLength = (bytes[3] << 24) | (bytes[2] << 16) | (bytes[1] << 8) | bytes[0];
            const nonce = bytes.subarray(4, 4 + nonceLength);
            const tagLength =
                (bytes[7 + nonceLength] << 24) | (bytes[6 + nonceLength] << 16) | (bytes[5 + nonceLength] << 8) | bytes[4 + nonceLength];
            const tag = bytes.subarray(8 + nonceLength, 8 + nonceLength + tagLength);
            const theRest = bytes.subarray(8 + nonceLength + tagLength);
            const cipherContent = this.concat(theRest, tag);
            const algoEncrypt = {
                name: "AES-GCM",
                iv: nonce,
                tagLength: tagLength * 8,
            };
            const decrypted = await crypto.subtle.decrypt(algoEncrypt, key, cipherContent);
            const decoder = new TextDecoder("utf-8");
            result = decoder.decode(decrypted);
        }
        return result;
    }

    getUserInfo(skipNotification = false) {
        const self: AuthService = this;
        return new Observable((observer) => {
            self.http.get<EncryptedPayload>(self.apiUrl.replace("/v2", "/v3") + "connect/userinfo").subscribe(
                (result) => {
                    this.decryptData(result.payload).then((decryptedresult) => {
                        self.userInfoFetched = true;
                        const decryptedresultjson = JSON.parse(decryptedresult);
                        self.currentUser = decryptedresultjson;
                        if (typeof self.currentUser.role === "string") {
                            self.currentUser.role = [self.currentUser.role];
                        }
                        // Added signal for migration
                        self._currentUser.set(self.currentUser);

                        if (!skipNotification) {
                            self.fetchedUserInfoSubject.next(true);
                        }
                        observer.next(decryptedresultjson);
                        observer.complete();
                    });
                },
                (err) => {
                    console.error("error getUserInfo", err);
                    observer.error(err);
                    if (err.status === 401) {
                        /* todo call self's logout method*/
                    }
                },
            );
        });
    }

    logout(preventReload = false): void {
        this.userLogoutSubject.next(null);
    }

    connectedUserCanManageSettings() {
        return this.currentUser.can_manage_general_settings == "True" || this.currentUser.can_manage_dashboards_management_settings == "True";
    }

    canManageGeneralSettings() {
        return this.currentUser.can_manage_general_settings == "True";
    }

    canManageDashboardManagementSettings() {
        return this.currentUser.can_manage_dashboards_management_settings == "True";
    }

    getCurrentUserToken() {
        const currentUser = this.currentUser;
        if (currentUser === null || currentUser === undefined) {
            return false;
        }

        return currentUser;
    }

    hasSecurityLevel(expectedRole: string): boolean {
        if (!this.currentUser) {
            return false;
        }

        return this.currentUser[expectedRole] == "True";
    }

    async beforeLongOperation(estimatedTime) {
        // Force an updateSession and wait
        this.keepalive.ping();
        await firstValueFrom(this.sessionUpdatedReceivedEvent$);
    }

    public addSingleSessionHeader(headers: HttpHeaders = null) {
        if (this.sessionData.site && this.sessionData.sessionName && this.sessionData.userId) {
            if (!headers) {
                headers = new HttpHeaders();
            }
            return headers
                .set("x-site", this.sessionData.site)
                .set("x-userid", this.sessionData.userId)
                .set("x-session-name", this.sessionData.sessionName);
        }
        return null;
    }

    bytesArrToHex(arr) {
        return [...arr].map((x) => x.toString(16).padStart(2, "0")).join("");
    }
}
